import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTutorial } from '../contexts/TutorialContext';
import { apiService } from '../services/api';
import { openProjectArtifactsModal } from '../PlatformShell';
import SiteProgressTracker from '../components/SiteProgressTracker';

// ---------------------------------------------------------------------------
// Tool info content for explainer modals
// ---------------------------------------------------------------------------

const TOOL_INFO: Record<string, { title: string; description: string; inputs: string; outputs: string }> = {
  connections: {
    title: 'Connections',
    description: 'Customer connection points are the foundational input for the entire pipeline. Each connection has GPS coordinates, a customer type (HH, SME, SCH, etc.), and a household count. Connections can be uploaded from a survey spreadsheet or placed manually by clicking on the map.',
    inputs: 'Survey spreadsheet (XLSX/CSV) with coordinates and customer types, or manual map clicks',
    outputs: 'Connection dataset feeding into exclusion zones, demand profiling, and network design',
  },
  d8760: {
    title: '8760 Demand Profile',
    description: 'Generates an 8760-hour (one full year) electricity demand profile from customer type counts using Monte Carlo synthesis. Each customer type has characteristic load shapes -- households peak in evening, businesses during day, schools mid-morning. The resulting hourly kW vector is the primary input for plant sizing optimization.',
    inputs: 'Customer type counts from connections dataset',
    outputs: 'Hourly demand vector (8760 values), peak kW, annual kWh',
  },
  ugridnet: {
    title: 'uGridNET -- Grid Design + Exclusion Zones',
    description: 'The network layout engine with integrated exclusion zone management. Auto-fetches OpenStreetMap building footprints, allows manual polygon drawing and KML/GeoJSON upload to define no-pole areas. Then uses constrained k-means clustering, minimum spanning tree (MST) algorithms, and pole placement optimization to design the full distribution network -- MV backbone, LV branches, service drops, and transformer locations. Calculates voltage drop, cable sizing, and network costs.',
    inputs: 'Connection coordinates, exclusion zones (OSM auto-fetch, drawn polygons, uploaded KML/GeoJSON), generation site location, config parameters',
    outputs: 'Poles, MV/LV lines, service drops, transformer locations, network cost estimate, voltage analysis',
  },
  ugrid: {
    title: 'uGrid -- Plant Sizing',
    description: 'Optimizes the generation plant design to minimize Levelized Cost of Electricity (LCOE). Fetches Typical Meteorological Year (TMY) weather data from EU PVGIS, then runs sizing optimization to determine optimal PV array, battery bank, and backup generator capacities. Includes gensite perimeter fencing costs and grid infrastructure costs from uGridNET.',
    inputs: '8760 demand profile, TMY weather data (auto-fetched), grid cost from uGridNET, economic parameters',
    outputs: 'PV kW, battery kWh, genset kW, LCOE $/kWh, CAPEX, annual OPEX, fence perimeter',
  },
  ugridplan: {
    title: 'uGridPLAN -- Construction Tracking',
    description: 'The core construction management tool. Visualizes the designed network on an interactive map, tracks construction progress through status codes (poles 0-9, connections 0-9, lines 0-5), manages the bill of materials, and enables field teams to update progress. Supports multi-project portfolio management with analytics dashboards.',
    inputs: 'Network layout from uGridNET, plant specs from uGrid, or existing project data',
    outputs: 'Construction progress data, BOM, KML exports for field teams, analytics',
  },
  ugridpredict: {
    title: 'uGridPREDICT -- Milestone Prediction',
    description:
      'Throughput and timeline tool: RET/ENG crews, GIS or manual rates, optional inventory/AM stock, deployment vs full progression (replenishments, per-month crews). Material need profile derives implied site material draws; Monte Carlo gives P10/P50/P90 months to complete. Exports JSON/CSV for stakeholders.',
    inputs: 'Site targets from uGridPLAN (pole count, line km, connection count), resource allocation',
    outputs: 'Monthly progress forecast, estimated completion date, work package timeline',
  },
};

function InfoModal({ nodeId, onClose }: { nodeId: string; onClose: () => void }) {
  const info = TOOL_INFO[nodeId];
  if (!info) return null;
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gray-900 text-white px-6 py-4">
          <h2 className="text-xl font-bold">{info.title}</h2>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-700 leading-relaxed">{info.description}</p>
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase mb-1">Inputs</h3>
            <p className="text-sm text-gray-600">{info.inputs}</p>
          </div>
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase mb-1">Outputs</h3>
            <p className="text-sm text-gray-600">{info.outputs}</p>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
          <Link
            to="/guide"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            onClick={onClose}
          >
            Platform User Guide &rarr;
          </Link>
          <button onClick={onClose} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dependency Wheel
// ---------------------------------------------------------------------------

interface WheelNode {
  id: string;
  label: string;
  sublabel: string;
  route: string;
  cx: number;
  cy: number;
  w: number;
  h: number;
  shape: 'oval' | 'rect';
  color: string;
  borderColor: string;
  textColor: string;
}

interface WheelEdge {
  from: string;
  to: string;
  label: string;
  path: string;  // hand-crafted SVG path
}

const W = 960;
const H = 640;

// 6 peer nodes in a hexagonal ring — no center node.
// Clockwise from 12: uGridNET → Connections → 8760 → uGrid → uGridPLAN → uGridPREDICT
// 12 / 2 o'clock: module *meaning* (label, route, id) is swapped vs the original wheel for
// cleaner arrows; each clock slot keeps its original *look* (shape + palette).
// Exclusions embedded in uGridNET, TMY embedded in uGrid.
const NODES: WheelNode[] = [
  // 12 o'clock — slate rect slot: uGridNET (content), Connections styling
  { id: 'ugridnet', label: 'uGridNET', sublabel: 'Grid Design + Exclusions',
    route: '/design-grid', cx: 480, cy: 95, w: 180, h: 76,
    shape: 'rect', color: '#1e293b', borderColor: '#475569', textColor: '#fff' },
  // 2 o'clock — blue oval slot: Connections (content), uGridNET styling
  { id: 'connections', label: 'Connections', sublabel: 'Upload or Draw',
    route: '/new-site', cx: 680, cy: 210, w: 180, h: 76,
    shape: 'oval', color: '#dbeafe', borderColor: '#2563eb', textColor: '#1e3a8a' },
  // 4 o'clock — 8760 Demand
  { id: 'd8760', label: '8760 Demand', sublabel: 'Hourly Load Profile',
    route: '/8760', cx: 680, cy: 430, w: 180, h: 76,
    shape: 'rect', color: '#ede9fe', borderColor: '#7c3aed', textColor: '#4c1d95' },
  // 6 o'clock — uGrid
  { id: 'ugrid', label: 'uGrid', sublabel: 'Plant Sizing + TMY',
    route: '/design-plant', cx: 480, cy: 545, w: 180, h: 76,
    shape: 'oval', color: '#f3e8ff', borderColor: '#9333ea', textColor: '#581c87' },
  // 8 o'clock — uGridPLAN
  { id: 'ugridplan', label: 'uGridPLAN', sublabel: 'Construction Tracking',
    route: '/planner', cx: 280, cy: 430, w: 180, h: 76,
    shape: 'rect', color: '#dcfce7', borderColor: '#16a34a', textColor: '#14532d' },
  // 10 o'clock — uGridPREDICT
  { id: 'ugridpredict', label: 'uGridPREDICT', sublabel: 'Milestone Prediction',
    route: '/predict', cx: 280, cy: 210, w: 180, h: 76,
    shape: 'oval', color: '#ffe4e6', borderColor: '#e11d48', textColor: '#881337' },
];

// Edges: adjacent pairs follow the ring, longer connections chord through empty center
const EDGES: WheelEdge[] = [
  // Adjacent: Connections(2) → uGridNET(12) — along ring (paths updated for swap)
  { from: 'connections', to: 'ugridnet', label: 'coordinates + exclusions',
    path: 'M 590 210 Q 520 165 480 133' },

  // Skip-1 chord: Connections(2) → 8760(4) — aligned x with 8760
  { from: 'connections', to: 'd8760', label: 'customer counts',
    path: 'M 680 248 Q 680 320 680 392' },

  // uGridNET(12) → uGrid(6) — vertical through center (shared cx)
  { from: 'ugridnet', to: 'ugrid', label: 'grid costs',
    path: 'M 480 133 Q 480 340 480 507' },

  // Adjacent: 8760(4) → uGrid(6) — clockwise along ring
  { from: 'd8760', to: 'ugrid', label: 'load profile',
    path: 'M 640 468 Q 590 510 530 540' },

  // Skip-2 chord: uGridNET(12) → uGridPLAN(8) — through center
  { from: 'ugridnet', to: 'ugridplan', label: 'poles, lines',
    path: 'M 400 125 Q 340 270 330 400' },

  // Adjacent: uGrid(6) → uGridPLAN(8) — counterclockwise along ring
  { from: 'ugrid', to: 'ugridplan', label: 'plant specs',
    path: 'M 420 555 Q 350 510 320 468' },

  // Adjacent: uGridPLAN(8) → uGridPREDICT(10) — counterclockwise along ring
  { from: 'ugridplan', to: 'ugridpredict', label: 'targets',
    path: 'M 280 392 Q 280 310 280 248' },
];

// --------------------------------------------------------------------------
// Manifest-driven node state
// --------------------------------------------------------------------------

/** Node -> manifest pipeline_state key that determines "done" state. */
const NODE_PRIMARY_STATE: Record<string, string> = {
  connections: 'connections_loaded',
  ugridnet: 'network_designed',
  d8760: 'demand_profiled',
  ugrid: 'sizing_complete',
  ugridplan: 'construction_started',
  ugridpredict: 'prediction_run',
};

/** Inbound dependencies per node (pipeline_state keys) — used to dim/gate nodes. */
const NODE_DEPS: Record<string, string[]> = {
  connections: [],
  ugridnet: ['connections_loaded'],
  d8760: ['connections_loaded'],
  ugrid: ['demand_profiled'], // TMY is fetched in-flow, not required to click in.
  // uGridPLAN: relaxed from `network_designed` -> `connections_loaded`.
  // The old gate created a catch-22 the user hit: uGridNET refused to
  // run without a generation site, /design-grid advised "go to
  // uGridPLAN to add a generation plant", but uGridPLAN itself was
  // gated behind the design step. Net result: no way to get the gen
  // site in once you'd loaded connections.
  //
  // uGridPLAN is the construction tracker -- it can usefully render
  // and accept edits (gen plants, transformers, exclusion zones,
  // manual pole placement) on a project that has connections but no
  // designed network. The previous gate over-stated the dependency.
  ugridplan: ['connections_loaded'],
  ugridpredict: ['construction_started'],
};

type NodeState = 'done' | 'ready' | 'blocked' | 'unknown';

interface NodeStateInfo {
  state: NodeState;
  unmet: string[];
}

function computeNodeState(
  nodeId: string,
  pipelineState: Record<string, boolean> | null,
): NodeStateInfo {
  if (!pipelineState) {
    return { state: 'unknown', unmet: [] };
  }
  const primaryKey = NODE_PRIMARY_STATE[nodeId];
  if (primaryKey && pipelineState[primaryKey]) {
    return { state: 'done', unmet: [] };
  }
  const deps = NODE_DEPS[nodeId] ?? [];
  const unmet = deps.filter((d) => !pipelineState[d]);
  if (unmet.length > 0) {
    return { state: 'blocked', unmet };
  }
  return { state: 'ready', unmet: [] };
}

interface DependencyWheelProps {
  onInfoClick: (id: string) => void;
  /** Manifest-derived state. null = no active project (renders in neutral palette). */
  pipelineState: Record<string, boolean> | null;
  /** Called when a blocked node is clicked — with a human-readable reason. */
  onBlockedClick?: (nodeId: string, reason: string) => void;
  /** Nodes intentionally disabled in this environment. */
  disabledNodeIds?: Set<string>;
  /** Called when a disabled node is clicked. */
  onDisabledClick?: (nodeId: string) => void;
  /** Whether the wheel is gated at all; false = behave like pre-workspace wheel. */
  gatingEnabled: boolean;
}

function DependencyWheel({
  onInfoClick,
  pipelineState,
  onBlockedClick,
  disabledNodeIds,
  onDisabledClick,
  gatingEnabled,
}: DependencyWheelProps) {
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const navigate = useNavigate();

  const isNodeHighlighted = (id: string) => {
    if (hoveredNode === id) return true;
    if (!hoveredEdge) return false;
    const [from, to] = hoveredEdge.split('->');
    return from === id || to === id;
  };

  const nodeStateById: Record<string, NodeStateInfo> = useMemo(() => {
    const out: Record<string, NodeStateInfo> = {};
    for (const node of NODES) {
      out[node.id] = computeNodeState(node.id, pipelineState);
    }
    return out;
  }, [pipelineState]);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxWidth: 960, height: 'auto' }}>
      <defs>
        <marker id="arr" markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
          <path d="M 0 0 L 10 4 L 0 8 Z" fill="#94a3b8" />
        </marker>
        <marker id="arr-active" markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
          <path d="M 0 0 L 10 4 L 0 8 Z" fill="#3b82f6" />
        </marker>
        <marker id="arr-done" markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
          <path d="M 0 0 L 10 4 L 0 8 Z" fill="#16a34a" />
        </marker>
      </defs>

      {/* Edges */}
      {EDGES.map(edge => {
        const key = `${edge.from}->${edge.to}`;
        const isActive = hoveredEdge === key || hoveredNode === edge.from || hoveredNode === edge.to;
        // Edge is "satisfied" when the upstream node is done (its producer ran).
        const fromState = nodeStateById[edge.from]?.state;
        const satisfied = gatingEnabled && fromState === 'done';
        const edgeColor = isActive
          ? '#3b82f6'
          : satisfied
            ? '#16a34a'
            : '#94a3b8';
        const marker = isActive
          ? 'url(#arr-active)'
          : satisfied
            ? 'url(#arr-done)'
            : 'url(#arr)';

        // Compute midpoint of the path for the label
        const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        pathEl.setAttribute('d', edge.path);
        let labelX = W / 2, labelY = H / 2;
        try {
          const totalLen = pathEl.getTotalLength();
          const pt = pathEl.getPointAtLength(totalLen * 0.5);
          labelX = pt.x;
          labelY = pt.y;
        } catch { /* fallback */ }

        return (
          <g key={key}
            onMouseEnter={() => setHoveredEdge(key)}
            onMouseLeave={() => setHoveredEdge(null)}
            style={{ cursor: 'default' }}
          >
            {/* Wider invisible hit area */}
            <path d={edge.path} fill="none" stroke="transparent" strokeWidth={20} />
            <path
              d={edge.path}
              fill="none"
              stroke={edgeColor}
              strokeWidth={isActive ? 4 : 3}
              strokeLinecap="round"
              markerEnd={marker}
              style={{ transition: 'stroke 0.15s, stroke-width 0.15s' }}
            />
            {isActive && (
              <text x={labelX} y={labelY - 8} textAnchor="middle"
                className="fill-blue-600 font-semibold select-none pointer-events-none"
                style={{ fontSize: 11 }}>
                {edge.label}
              </text>
            )}
          </g>
        );
      })}

      {/* Nodes */}
      {NODES.map(node => {
        const highlighted = isNodeHighlighted(node.id);
        const halfW = node.w / 2;
        const halfH = node.h / 2;
        const info = nodeStateById[node.id] || { state: 'unknown', unmet: [] };
        const isDisabled = !!disabledNodeIds?.has(node.id);

        // Visual treatment:
        //   done    -> full color + green outline accent (ring shadow below).
        //   ready   -> full color, normal outline (identical to pre-workspace look).
        //   blocked -> dimmed (reduced opacity) + dashed gray outline.
        //   unknown -> no project loaded: treat as 'ready' (backward-compatible).
        const effectiveState: NodeState = !gatingEnabled ? 'ready' : info.state;
        const dimmed = effectiveState === 'blocked' || isDisabled;
        const showDoneRing = effectiveState === 'done';
        const strokeColor = dimmed ? '#94a3b8' : (highlighted ? '#1d4ed8' : node.borderColor);
        const strokeWidth = highlighted ? 3.5 : 2.5;
        const strokeDasharray = dimmed ? '6 4' : undefined;
        const opacity = dimmed ? 0.55 : 1;

        const renderShape = () => node.shape === 'oval' ? (
          <ellipse
            cx={node.cx} cy={node.cy} rx={halfW} ry={halfH}
            fill={node.color}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            opacity={opacity}
            style={{
              transition: 'all 0.15s',
              cursor: 'pointer',
              filter: highlighted ? 'drop-shadow(0 4px 16px rgba(0,0,0,0.25))' : 'none',
            }}
          />
        ) : (
          <rect
            x={node.cx - halfW} y={node.cy - halfH}
            width={node.w} height={node.h}
            rx={12} ry={12}
            fill={node.color}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            opacity={opacity}
            style={{
              transition: 'all 0.15s',
              cursor: 'pointer',
              filter: highlighted ? 'drop-shadow(0 4px 16px rgba(0,0,0,0.25))' : 'none',
            }}
          />
        );

        const nodeInner = (
          <>
            {showDoneRing && (
              node.shape === 'oval'
                ? <ellipse cx={node.cx} cy={node.cy} rx={halfW + 5} ry={halfH + 5}
                    fill="none" stroke="#16a34a" strokeWidth={2.5}
                    strokeDasharray="2 2" opacity={0.85} />
                : <rect x={node.cx - halfW - 5} y={node.cy - halfH - 5}
                    width={node.w + 10} height={node.h + 10}
                    rx={14} ry={14}
                    fill="none" stroke="#16a34a" strokeWidth={2.5}
                    strokeDasharray="2 2" opacity={0.85} />
            )}
            {renderShape()}
            {/* Label */}
            <text x={node.cx} y={node.cy - 4} textAnchor="middle"
              fill={node.textColor} className="font-bold select-none pointer-events-none"
              opacity={opacity}
              style={{ fontSize: 14 }}>
              {node.label}
            </text>
            <text x={node.cx} y={node.cy + 14} textAnchor="middle"
              fill={node.textColor} className="select-none pointer-events-none"
              opacity={opacity * 0.7}
              style={{ fontSize: 11 }}>
              {node.sublabel}
            </text>
            {showDoneRing && (
              <text x={node.cx + halfW - 26} y={node.cy + halfH - 6}
                textAnchor="middle" fill="#16a34a"
                className="font-bold select-none pointer-events-none"
                style={{ fontSize: 11 }}>
                ✓ done
              </text>
            )}
          </>
        );

        return (
          <g
            key={node.id}
            onMouseEnter={() => setHoveredNode(node.id)}
            onMouseLeave={() => setHoveredNode(null)}
            data-tutorial={node.id === 'connections' ? 'tutorial-wheel-connections' : undefined}
          >
            {isDisabled ? (
              <g
                style={{ cursor: 'not-allowed' }}
                onClick={() => onDisabledClick?.(node.id)}
              >
                {nodeInner}
              </g>
            ) : dimmed ? (
              <g
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const reason = `Needs ${info.unmet.map((u) => u.replace(/_/g, ' ')).join(', ')} first`;
                  if (onBlockedClick) {
                    onBlockedClick(node.id, reason);
                  } else {
                    // Allow navigation even if gated — keeps the tool still reachable.
                    navigate(node.route);
                  }
                }}
              >
                {nodeInner}
              </g>
            ) : (
              <Link to={node.route}>{nodeInner}</Link>
            )}

            {/* Info icon (top-right of shape) */}
            <g
              onClick={(e) => { e.stopPropagation(); onInfoClick(node.id); }}
              style={{ cursor: 'pointer' }}
            >
              <circle
                cx={node.cx + halfW - 8} cy={node.cy - halfH + 8}
                r={10}
                fill="white" stroke={node.borderColor} strokeWidth={1.5}
              />
              <text
                x={node.cx + halfW - 8} y={node.cy - halfH + 12}
                textAnchor="middle" fill={node.borderColor}
                className="font-bold select-none" style={{ fontSize: 12 }}>
                i
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Pipeline detail (collapsible)
// ---------------------------------------------------------------------------

interface PipelineStep {
  id: number; name: string; description: string;
  status: 'manual' | 'integrated'; route?: string; icon: string;
  phase: 'upstream' | 'design' | 'construction' | 'sizing';
}

const PIPELINE_STEPS: PipelineStep[] = [
  { id: 0, name: 'Setting Up Tablets', description: 'Configure ODK Collect on field tablets.', status: 'manual', icon: '📱', phase: 'upstream' },
  { id: 1, name: 'Pre-Survey Preparation', description: 'Print maps and site forms.', status: 'manual', icon: '🗺️', phase: 'upstream' },
  { id: 2, name: 'Site Survey', description: 'Field survey: annotate maps, collect coordinates.', status: 'manual', icon: '📋', phase: 'upstream' },
  { id: 3, name: 'Audit', description: 'Verify survey data against annotated maps.', status: 'manual', icon: '✅', phase: 'upstream' },
  { id: 4, name: 'Exclusion Zones', description: 'Define no-pole areas using OSM data.', status: 'integrated', route: '/exclusions', icon: '🚫', phase: 'upstream' },
  { id: 5, name: 'uGridNET Layouts', description: 'Generate village-level network designs.', status: 'integrated', route: '/design-grid', icon: '🔌', phase: 'design' },
  { id: 6, name: 'uGridPLAN Export', description: 'Load network and export KMLs.', status: 'integrated', route: '/planner', icon: '📦', phase: 'design' },
  { id: 7, name: 'Desktop Review', description: 'Review design, compile edits.', status: 'integrated', route: '/planner', icon: '🖥️', phase: 'design' },
  { id: 8, name: 'Create Supercluster', description: 'Combine village data.', status: 'integrated', route: '/design-grid', icon: '🔗', phase: 'design' },
  { id: 9, name: 'Generate Backbone', description: 'MV backbone from centroids.', status: 'integrated', route: '/design-grid', icon: '🦴', phase: 'design' },
  { id: 10, name: 'Backbone-Village Merge', description: 'Merge backbone with villages.', status: 'integrated', route: '/design-grid', icon: '🔀', phase: 'design' },
  { id: 11, name: 'Determine Clustering', description: 'Cluster concessions <5km apart.', status: 'integrated', route: '/design-grid', icon: '🏘️', phase: 'design' },
  { id: 12, name: 'Remove Irregularities', description: 'Fix polygons and parallel lines.', status: 'integrated', route: '/planner', icon: '🔧', phase: 'construction' },
  { id: 13, name: 'Desktop Review (SC)', description: 'Reticulation team review.', status: 'integrated', route: '/planner', icon: '👷', phase: 'construction' },
  { id: 14, name: 'Download TMY', description: 'Auto-fetch from EU PVGIS.', status: 'integrated', route: '/design-plant', icon: '🌤️', phase: 'sizing' },
  { id: 15, name: 'Demand Projection (8760)', description: 'Hourly demand via Monte Carlo.', status: 'integrated', route: '/8760', icon: '📊', phase: 'sizing' },
  { id: 16, name: 'uGrid Sizing', description: 'Optimize PV/battery/genset via PSO.', status: 'integrated', route: '/design-plant', icon: '⚡', phase: 'sizing' },
];

const PHASE_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
  upstream: { label: 'Upstream (Survey & Prep)', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  design: { label: 'Network Design', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  construction: { label: 'Construction Tracking', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
  sizing: { label: 'Plant Sizing', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200' },
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ProcessFlowHub() {
  const tutorial = useTutorial();
  const [showPipeline, setShowPipeline] = useState(false);
  const [infoNode, setInfoNode] = useState<string | null>(null);

  // Try to resolve the "currently active" project (last loaded in this browser).
  // If no key is set in localStorage, we leave pipelineState=null and the wheel
  // renders in the original neutral palette (gating disabled).
  const [activeProjectKey, setActiveProjectKey] = useState<string | null>(null);
  const [activeProjectState, setActiveProjectState] = useState<'draft' | 'published' | null>(null);
  const [pipelineState, setPipelineState] = useState<Record<string, boolean> | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [blockedToast, setBlockedToast] = useState<{ nodeId: string; reason: string } | null>(null);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);

  const refreshManifest = useCallback(async (key: string) => {
    try {
      setManifestError(null);
      const result = await apiService.getProjectManifest(key);
      setPipelineState(result.manifest?.pipeline_state || {});
      setActiveProjectState(result.state);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not load manifest';
      setManifestError(typeof msg === 'string' ? msg : 'Could not load manifest');
      setPipelineState({});
    }
  }, []);

  useEffect(() => {
    try {
      const key = localStorage.getItem('ugp_active_project_key');
      setActiveProjectKey(key);
      if (key) {
        void refreshManifest(key);
      } else {
        setPipelineState(null);
      }
    } catch {
      setPipelineState(null);
    }
  }, [refreshManifest]);

  useEffect(() => {
    if (!blockedToast) return;
    const t = window.setTimeout(() => setBlockedToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [blockedToast]);

  const gatingEnabled = !!activeProjectKey;
  const disabledNodeIds = useMemo(() => {
    if (typeof window === 'undefined') return new Set<string>();
    const isProd = window.location.hostname === 'ugp.1pwrafrica.com';
    return isProd ? new Set<string>(['ugridpredict']) : new Set<string>();
  }, []);

  return (
    <div className="min-h-full bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 pt-8 pb-4">
            <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold tracking-tight mb-1">Minigrid Design Platform</h1>
              <p className="text-gray-400 text-base max-w-xl">
                End-to-end workflow from site survey through network design, plant sizing, construction tracking, and progress prediction.
              </p>
              <p className="mt-3 text-sm text-gray-300 max-w-xl">
                <Link
                  to="/guide"
                  className="font-semibold text-white underline decoration-sky-400/80 underline-offset-2 hover:text-sky-200"
                >
                  User guide & documentation
                </Link>
                <span className="text-gray-500">
                  {' '}
                  — searchable topics (same as{' '}
                  <span className="text-gray-400">📖</span> next to the logo and the{' '}
                  <span className="text-gray-400">📖 Help</span> tab in the top navigation).
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to="/guide"
                className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium border border-white/20 transition-colors whitespace-nowrap"
              >
                📖 User guide
              </Link>
              <button
                onClick={tutorial.start}
                className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium border border-white/20 transition-colors whitespace-nowrap"
              >
                Take the Guided Tour
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active project banner */}
      {activeProjectKey && (
        <div className="max-w-5xl mx-auto px-6 pt-4 -mb-2">
          <div className="flex items-center justify-between text-xs bg-white rounded-lg border px-3 py-2 text-gray-600">
            <span>
              Pipeline state shown for{' '}
              <span className="font-semibold text-gray-900">{activeProjectKey}</span>
              {activeProjectState && (
                <span
                  className={`ml-2 px-1.5 py-0.5 rounded uppercase tracking-wider text-[10px] ${
                    activeProjectState === 'draft'
                      ? 'bg-violet-100 text-violet-700'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {activeProjectState}
                </span>
              )}
            </span>
            <div className="flex items-center gap-3">
              {manifestError && (
                <span className="text-amber-700">{manifestError}</span>
              )}
              {/*
                Project Artifacts modal trigger. Opens the global
                modal mounted in PlatformShell -- which calls
                /projects/{name}/artifacts and shows every saved
                dataset slot, every 8760 run, and click-through
                links to each tool. Closes the historical
                "where exactly is my work saved" question with a
                single comprehensive view.
              */}
              <button
                type="button"
                onClick={openProjectArtifactsModal}
                className="text-emerald-700 hover:text-emerald-900 underline font-medium"
                title="See every dataset, run, and design saved to this project"
              >
                📋 Saved artifacts…
              </button>
              <button
                type="button"
                onClick={() => activeProjectKey && refreshManifest(activeProjectKey)}
                className="text-sky-700 hover:text-sky-900 underline"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dependency Wheel */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="bg-white rounded-2xl shadow-sm border p-4">
          <p className="text-center text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">
            Click any node to open tool &mdash; hover arrows to see data flow &mdash; click <strong>i</strong> for details
          </p>
          <DependencyWheel
            onInfoClick={setInfoNode}
            pipelineState={pipelineState}
            gatingEnabled={gatingEnabled}
            disabledNodeIds={disabledNodeIds}
            onBlockedClick={(nodeId, reason) =>
              setBlockedToast({ nodeId, reason })
            }
            onDisabledClick={(nodeId) => {
              if (nodeId === 'ugridpredict') {
                setShowComingSoonModal(true);
              }
            }}
          />
          {gatingEnabled && (
            <div className="mt-3 flex items-center justify-center gap-4 text-[11px] text-gray-500">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full ring-2 ring-green-500 ring-offset-1 bg-white" /> done
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full bg-gray-200" /> ready
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full border border-dashed border-gray-400" /> blocked (dependency missing)
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Blocked-click toast */}
      {blockedToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[2500] bg-amber-500 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-lg">
          {blockedToast.reason}
        </div>
      )}
      {showComingSoonModal && (
        <div
          className="fixed inset-0 z-[2600] flex items-center justify-center bg-black/40 px-4"
          onClick={() => setShowComingSoonModal(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900">Coming Soon</h3>
            <p className="mt-2 text-sm text-gray-700">
              uGridPREDICT is temporarily unavailable in production while we finalize rollout.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              You can continue using all other modules in the platform.
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                onClick={() => setShowComingSoonModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio progress rollup (excludes uGridPREDICT by design) */}
      <SiteProgressTracker />

      {/* Pipeline Detail (collapsible) */}
      <div className="max-w-7xl mx-auto px-6 pb-10">
        <button
          onClick={() => setShowPipeline(!showPipeline)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 mb-4"
        >
          <span className={`transition-transform ${showPipeline ? 'rotate-90' : ''}`}>&#9654;</span>
          MGD044V04 Design Process Pipeline ({PIPELINE_STEPS.length} steps)
        </button>

        {showPipeline && (
          <div>
            {(['upstream', 'design', 'construction', 'sizing'] as const).map(phase => {
              const meta = PHASE_META[phase];
              const steps = PIPELINE_STEPS.filter(s => s.phase === phase);
              return (
                <div key={phase} className="mb-6">
                  <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${meta.bg} ${meta.color} border ${meta.border}`}>
                    {meta.label}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {steps.map(step => {
                      const inner = (
                        <div className={`border rounded-lg p-3 text-sm h-full ${
                          step.route ? 'hover:shadow-md hover:border-blue-400 cursor-pointer border-gray-200' : 'border-gray-100 opacity-75'
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span>{step.icon}</span>
                            <span className="font-semibold text-gray-900">{step.name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ml-auto ${
                              step.status === 'integrated' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                            }`}>{step.status === 'integrated' ? 'Live' : 'Manual'}</span>
                          </div>
                          <p className="text-xs text-gray-500">{step.description}</p>
                        </div>
                      );
                      return step.route
                        ? <Link key={step.id} to={step.route}>{inner}</Link>
                        : <div key={step.id}>{inner}</div>;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Modal */}
      {infoNode && <InfoModal nodeId={infoNode} onClose={() => setInfoNode(null)} />}
    </div>
  );
}
